import React, { PureComponent } from 'react';
import _get from 'lodash.get';
import { ModelService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { createOptionalContext } from '../../../utils/create-optional-context.js';
const [FormContext, FormContextConsumer, FormContextProvider, useForm,] = createOptionalContext('FormContext');
function areValidationsValid(validations) {
    return !validations.some(validation => !validation.valid);
}
class FormProvider extends PureComponent {
    constructor() {
        super(...arguments);
        this.entryChildValidations = new Map();
        this.entryValidators = new Map();
        this.nestedValidates = new Set();
        this.resetListeners = new Set();
        this.validateListeners = new Set();
        this.notifyListeners = (listeners) => {
            for (const listener of listeners) {
                listener();
            }
        };
        this.notifyResetListeners = () => {
            this.notifyListeners(this.resetListeners);
        };
        this.notifyValidateListeners = () => {
            this.notifyListeners(this.validateListeners);
        };
        this.addListener = (listeners, fn) => {
            if (listeners.has(fn)) {
                throw Error('[Form Context]: This listener was already added.');
            }
            listeners.add(fn);
            return () => {
                listeners.delete(fn);
            };
        };
        /**
         * Add a validate function to be called when the context is validated,
         * also using it to confirm if a given record is valid.
         *
         * @param fn - The function that will be called when the validation is run.
         * @returns A function to remove the added validation function.
         */
        this.addNestedValidate = (fn) => {
            if (this.nestedValidates.has(fn)) {
                throw Error('[Form Context]: This nested validate was already added.');
            }
            this.nestedValidates.add(fn);
            return () => {
                this.nestedValidates.delete(fn);
            };
        };
        /**
         * Add a new listener that will be called everytime that a form reset is called.
         *
         * @param fn - The listener to be added.
         * @returns A function to remove the added listener.
         */
        this.addResetListener = (fn) => {
            return this.addListener(this.resetListeners, fn);
        };
        /**
         * Add a new listener that will be called everytime that the form validation is completed.
         *
         * @param fn - The listener to be added.
         * @returns A function to remove the added listener.
         */
        this.addValidateListener = (fn) => {
            return this.addListener(this.validateListeners, fn);
        };
        /**
         * Resets all child validations of a given entry.
         *
         * @param entry - The entry to be reseted.
         */
        this.resetEntryChildValidations = (entry) => {
            this.entryChildValidations.set(entry, []);
        };
        /**
         * Updates the child validations of a given entry.
         *
         * @param entry - The entry to be uppdated;
         * @param validations - The updated validations.
         */
        this.updateEntryChildValidations = (entry, validations) => {
            this.entryChildValidations.set(entry, validations);
        };
        /**
         * Clears all validators of a given entry.
         *
         * @param entry - The entry to be cleared.
         */
        this.removeEntryValidators = (entry) => {
            this.entryValidators.delete(entry);
        };
        /**
         * Updates all validators of a given entry.
         *
         * @param entry - The entry to be updated;
         * @param validators - The updated validators.
         */
        this.updateEntryValidators = (entry, validators) => {
            this.entryValidators.set(entry, validators);
        };
        /**
         * Obtain all validations of a given entry and value, running all registered validators against it.
         *
         * @param entry - The entry to be used;
         * @param value - The value to use in the validations;
         * @param onlyFocus - If only the focus validators should be used.
         * @returns A list of all entry validation results.
         */
        this.getEntryValidations = (entry, value, onlyFocus = false) => {
            const validators = this.entryValidators.get(entry) ?? [];
            const runner = ModelService.validate(value);
            const localValidations = [];
            for (const validator of validators) {
                if (onlyFocus && !validator.options?.runOnFocus) {
                    continue;
                }
                if ('validate' in validator) {
                    localValidations.push(...validator.validate(value, validator.options));
                    continue;
                }
                runner.apply(validator.name, validator.options);
            }
            const standardValidations = runner.run();
            const childValidations = this.entryChildValidations.get(entry) ?? [];
            return [...localValidations, ...standardValidations, ...childValidations];
        };
        /**
         * Notify all form children that they should be reseted to their initial state.
         */
        this.reset = () => {
            this.notifyResetListeners();
        };
        /**
         * Validate a given record.
         *
         * @param record - The record to be validated;
         * @param notify - If the listeners should be notified after the validation.
         * @returns An object containing the validation result and all validations.
         */
        this.validate = (record, notify = false) => {
            const entryValidations = {};
            for (const [entry] of this.entryValidators) {
                const value = _get(record, entry);
                entryValidations[entry] = this.getEntryValidations(entry, value);
            }
            if (notify) {
                this.notifyValidateListeners();
            }
            const validations = Object.values(entryValidations).flat();
            for (const validate of this.nestedValidates) {
                const [, nestedValidations] = validate();
                validations.push(...nestedValidations);
            }
            const valid = areValidationsValid(validations);
            return [valid, validations];
        };
        /**
         * Validates a given entry.
         *
         * @param entry - The entry to be used;
         * @param value - The value to use in the validations;
         * @param onlyFocus - If only the focus validators should be used.
         * @returns An object containing the validation result and all validations.
         */
        this.validateEntry = (entry, value, onlyFocus = false) => {
            const validations = this.getEntryValidations(entry, value, onlyFocus);
            const valid = areValidationsValid(validations);
            return [valid, validations];
        };
    }
    render() {
        const context = {
            addNestedValidate: this.addNestedValidate,
            addResetListener: this.addResetListener,
            addValidateListener: this.addValidateListener,
            getEntryValidations: this.getEntryValidations,
            removeEntryValidators: this.removeEntryValidators,
            reset: this.reset,
            resetEntryChildValidations: this.resetEntryChildValidations,
            updateEntryChildValidations: this.updateEntryChildValidations,
            updateEntryValidators: this.updateEntryValidators,
            validate: this.validate,
            validateEntry: this.validateEntry,
        };
        return (React.createElement(FormContextProvider, { value: context }, this.props.children));
    }
}
export { FormContext, FormContextConsumer, FormProvider, useForm, };
//# sourceMappingURL=form-context.js.map